import React, { useState, useEffect, useRef, useContext } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../config/axios';
import { AuthContext } from '../context/AuthContext';
import { formatError } from '../utils/errorHandler';
import { formatRoleLabel } from '../utils/roleLabels';
import UserAvatar from '../components/UserAvatar';
import ImageCropper from '../components/ImageCropper';
import EmptyState from '../components/EmptyState';
import LoadingSpinner from '../components/LoadingSpinner';
import CreateLocationModal from '../components/CreateLocationModal';
import Modal from '../components/Modal';
import UpgradeCta from '../components/UpgradeCta';
import { canInviteUsers, getUserCapLabel } from '../utils/planAccess';
import { useConfirm } from '../context/ConfirmContext';
import './Tasks.css';
import './Users.css';

const Users = () => {
  const { t } = useTranslation(['users', 'common']);
  const confirm = useConfirm();
  const { user: currentUser } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: '',
    companyId: '',
    role: 'employee',
    isActive: true,
    locations: ''
  });
  const [error, setError] = useState('');
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [removeImage, setRemoveImage] = useState(false);
  const profileImageInputRef = useRef(null);
  const [locationSearch, setLocationSearch] = useState('');
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [showCreateLocationModal, setShowCreateLocationModal] = useState(false);
  const [newLocationData, setNewLocationData] = useState({ name: '', address: '' });
  const locationInputRef = useRef(null);
  const locationDropdownRef = useRef(null);

  useEffect(() => {
    fetchUsers();
    fetchCompanies();
    fetchLocations();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/api/users');
      setUsers(response.data.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError(t('users:failedFetch'));
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await axios.get('/api/companies');
      setCompanies(response.data.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get('/api/locations');
      setLocations(response.data.data);
      // If editing a user, re-initialize selected locations with the fetched locations
      if (editingUser && formData.locations) {
        const locationNames = formData.locations.split(',').map(name => name.trim()).filter(Boolean);
        const matched = response.data.data.filter(loc => 
          locationNames.some(name => loc.name.toLowerCase() === name.toLowerCase())
        );
        setSelectedLocations(matched);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  // Filter locations based on search input
  const filteredLocations = locationSearch
    ? locations.filter(loc =>
        loc.name.toLowerCase().includes(locationSearch.toLowerCase())
      )
    : locations;

  // Check if search input matches any existing location
  const exactMatch = locations.some(
    loc => loc.name.toLowerCase() === locationSearch.toLowerCase().trim()
  );

  // Check if any locations match the search
  const hasMatches = filteredLocations.length > 0;

  // Handle location selection
  const handleLocationSelect = (location) => {
    if (!selectedLocations.some(loc => loc.id === location.id)) {
      const updated = [...selectedLocations, location];
      setSelectedLocations(updated);
      updateLocationsString(updated);
    }
    setLocationSearch('');
    setShowLocationDropdown(false);
  };

  // Remove selected location
  const handleRemoveLocation = (locationId) => {
    const updated = selectedLocations.filter(loc => loc.id !== locationId);
    setSelectedLocations(updated);
    updateLocationsString(updated);
  };

  // Update the locations string in formData
  const updateLocationsString = (locationArray) => {
    const locationsString = locationArray.map(loc => loc.name).join(', ');
    setFormData(prev => ({
      ...prev,
      locations: locationsString
    }));
  };

  // Handle location search input
  const handleLocationSearchChange = (e) => {
    const value = e.target.value;
    setLocationSearch(value);
    setShowLocationDropdown(true);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        locationDropdownRef.current &&
        !locationDropdownRef.current.contains(event.target) &&
        locationInputRef.current &&
        !locationInputRef.current.contains(event.target)
      ) {
        setShowLocationDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      setRemoveImage(false);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCropComplete = (croppedFile) => {
    setProfileImageFile(croppedFile);
    setShowCropper(false);
    // Update preview with cropped image
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
    };
    reader.readAsDataURL(croppedFile);
  };

  const handleCancelCrop = () => {
    setShowCropper(false);
    setImagePreview(null);
    setProfileImageFile(null);
    if (profileImageInputRef.current) {
      profileImageInputRef.current.value = '';
    }
  };

  const handleRemoveImage = () => {
    setRemoveImage(true);
    setProfileImageFile(null);
    setImagePreview(null);
    if (profileImageInputRef.current) {
      profileImageInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (editingUser) {
        const payload = new FormData();
        payload.append('email', formData.email);
        payload.append('firstName', formData.firstName);
        payload.append('lastName', formData.lastName);
        payload.append('phone', formData.phone || '');
        payload.append('role', formData.role);
        payload.append('isActive', formData.isActive);
        if (formData.password) {
          payload.append('password', formData.password);
        }
        if (formData.companyId) {
          payload.append('companyId', formData.companyId);
        }
        if (formData.locations !== undefined) {
          payload.append('locations', formData.locations);
        }
        if (removeImage) {
          payload.append('removeProfileImage', 'true');
        } else if (profileImageFile) {
          payload.append('profileImage', profileImageFile);
        }
        await axios.put(`/api/users/${editingUser.id}`, payload);
      } else {
        const payload = new FormData();
        payload.append('email', formData.email);
        payload.append('password', formData.password);
        payload.append('firstName', formData.firstName);
        payload.append('lastName', formData.lastName);
        payload.append('phone', formData.phone || '');
        payload.append('role', formData.role);
        payload.append('isActive', formData.isActive);
        if (formData.companyId) {
          payload.append('companyId', formData.companyId);
        }
        if (formData.locations) {
          payload.append('locations', formData.locations);
        }
        if (profileImageFile) {
          payload.append('profileImage', profileImageFile);
        }
        await axios.post('/api/users', payload);
      }
      setShowModal(false);
      setEditingUser(null);
      setFormData({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        phone: '',
        companyId: '',
        role: 'employee',
        isActive: true,
        locations: ''
      });
      setProfileImageFile(null);
      setImagePreview(null);
      setRemoveImage(false);
      setShowCropper(false);
      setSelectedLocations([]);
      setLocationSearch('');
      setShowLocationDropdown(false);
      if (profileImageInputRef.current) {
        profileImageInputRef.current.value = '';
      }
      fetchUsers();
    } catch (error) {
      setError(
        formatError(error) ||
          (editingUser ? t('users:failedUpdate') : t('users:failedCreate'))
      );
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    const userLocations = user.locations || '';
    setFormData({
      email: user.email || '',
      password: '',
      firstName: user.first_name || '',
      lastName: user.last_name || '',
      phone: user.phone || '',
      companyId: user.company_id || '',
      role: user.role || 'employee',
      isActive: user.is_active !== false,
      locations: userLocations
    });
    // Initialize selected locations from the locations string
    if (userLocations && locations.length > 0) {
      const locationNames = userLocations.split(',').map(name => name.trim()).filter(Boolean);
      const matched = locations.filter(loc => 
        locationNames.some(name => loc.name.toLowerCase() === name.toLowerCase())
      );
      setSelectedLocations(matched);
    } else {
      setSelectedLocations([]);
    }
    setProfileImageFile(null);
    setImagePreview(null);
    setRemoveImage(false);
    setShowCropper(false);
    if (profileImageInputRef.current) {
      profileImageInputRef.current.value = '';
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      phone: '',
      companyId: '',
      role: 'employee',
      isActive: true,
      locations: ''
    });
    setSelectedLocations([]);
    setLocationSearch('');
    setShowLocationDropdown(false);
  };

  const handleDelete = async (id) => {
    if (!(await confirm(t('users:confirmDelete'), { danger: true }))) {
      return;
    }

    try {
      await axios.delete(`/api/users/${id}`);
      fetchUsers();
    } catch (error) {
      setError(formatError(error) || t('users:failedDelete'));
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="users">
      <div className="container container--wide main-content">
        <div className="page-header">
          <div className="users-header-main">
            {(currentUser?.active_company_role === 'admin' || currentUser?.role === 'admin') &&
              currentUser?.role !== 'super_admin' && (
              <Link to="/my-company" className="users-back-link">
                {t('users:backToCompany')}
              </Link>
            )}
            <h1>{t('users:pageTitle')}</h1>
          </div>
          <div className="users-create-actions">
            {currentUser?.role === 'admin' && (
              <span className="users-cap-label">{getUserCapLabel(currentUser, t)}</span>
            )}
            <button
              type="button"
              className="btn btn-primary tasks-header-create-btn"
              onClick={() => setShowModal(true)}
              disabled={!canInviteUsers(currentUser)}
            >
              <span className="material-symbols-outlined" aria-hidden="true">add</span>
              {t('users:createUser')}
            </button>
          </div>
        </div>

        {!canInviteUsers(currentUser) && <UpgradeCta className="error" />}
        {error && <div className="error">{error}</div>}

        {users.length > 0 ? (
          <>
            <div className="tasks-list-table-wrap tasks-list-table-desktop">
              <table className="table tasks-list-table">
                <thead>
                  <tr>
                    <th>{t('common:name')}</th>
                    <th>{t('common:email')}</th>
                    <th>{t('common:phone')}</th>
                    <th>{t('common:role')}</th>
                    <th>{t('common:company')}</th>
                    <th>{t('common:locations')}</th>
                    <th>{t('common:status')}</th>
                    <th>{t('common:actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr
                      key={user.id}
                      className="tasks-list-table-row"
                      onClick={() => handleEdit(user)}
                    >
                      <td>
                        <div className="users-table-name-cell">
                          <UserAvatar user={user} size={32} />
                          <span className="tasks-list-table-title">{user.first_name} {user.last_name}</span>
                        </div>
                      </td>
                      <td>{user.email}</td>
                      <td>{user.phone || t('common:emptyCell')}</td>
                      <td>{formatRoleLabel(user.role, t)}</td>
                      <td>{user.company_name || t('common:emptyCell')}</td>
                      <td>{user.locations || t('common:emptyCell')}</td>
                      <td>
                        <span className={`users-status-badge ${user.is_active ? 'users-status-badge--active' : 'users-status-badge--inactive'}`}>
                          {user.is_active ? t('common:active') : t('common:inactive')}
                        </span>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="action-buttons">
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => handleEdit(user)}
                          >
                            {t('common:edit')}
                          </button>
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDelete(user.id)}
                          >
                            {t('common:delete')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="tasks-card-grid tasks-list-cards-mobile">
              {users.map((user) => (
                <article
                  key={user.id}
                  className="tasks-redesign-card"
                  onClick={() => handleEdit(user)}
                >
                  <div className="tasks-redesign-card-body">
                    <div className="tasks-redesign-card-top">
                      <div className="users-table-name-cell">
                        <UserAvatar user={user} size={32} />
                        <h3 className="tasks-redesign-card-title">{user.first_name} {user.last_name}</h3>
                      </div>
                      <span className={`users-status-badge ${user.is_active ? 'users-status-badge--active' : 'users-status-badge--inactive'}`}>
                        {user.is_active ? t('common:active') : t('common:inactive')}
                      </span>
                    </div>
                    <div className="tasks-redesign-card-meta users-mobile-card-meta-row">
                      <span className="tasks-redesign-meta-item">{user.email}</span>
                      <span className="tasks-redesign-meta-item">{formatRoleLabel(user.role, t)}</span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </>
        ) : (
          <EmptyState
            icon="group"
            title={t('users:noUsersTitle')}
            description={t('users:noUsersDescription')}
            actionLabel={canInviteUsers(currentUser) ? t('users:createUser') : undefined}
            onAction={canInviteUsers(currentUser) ? () => setShowModal(true) : undefined}
          />
        )}

        <Modal
          isOpen={showModal}
          onClose={handleCloseModal}
          title={editingUser ? t('users:editUser') : t('users:createUser')}
          className="users-modal-panel"
        >
          <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>
                    {editingUser ? t('users:profileImage') : t('users:profileImageOptional')}
                  </label>
                  <div className="users-modal-avatar-row">
                    <div>
                      {imagePreview ? (
                        <img
                          src={imagePreview}
                          alt={t('users:previewAlt')}
                          className="users-modal-avatar-preview"
                        />
                      ) : (
                        <UserAvatar user={editingUser || {}} size={80} />
                      )}
                    </div>
                    <div className="users-modal-avatar-controls">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        ref={profileImageInputRef}
                        className="users-modal-file-input"
                      />
                      <div className="users-modal-image-actions">
                        {editingUser?.profile_image_url && !removeImage && (
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={handleRemoveImage}
                          >
                            {t('users:removeImage')}
                          </button>
                        )}
                        {imagePreview && (
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => {
                              setImagePreview(null);
                              setProfileImageFile(null);
                              setRemoveImage(false);
                              if (profileImageInputRef.current) {
                                profileImageInputRef.current.value = '';
                              }
                            }}
                          >
                            {t('common:cancel')}
                          </button>
                        )}
                      </div>
                      {removeImage && (
                        <small className="users-field-error">
                          {t('users:imageRemovedOnSave')}
                        </small>
                      )}
                    </div>
                  </div>
                </div>
                {showCropper && profileImageInputRef.current?.files?.[0] && (
                  <ImageCropper
                    imageFile={profileImageInputRef.current.files[0]}
                    onCropComplete={handleCropComplete}
                    onCancel={handleCancelCrop}
                    aspect={1}
                  />
                )}
                <div className="form-group">
                  <label>{t('users:firstNameLabel')}</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>{t('users:lastNameLabel')}</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>{t('users:emailLabel')}</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>
                    {editingUser ? t('users:passwordKeepCurrent') : t('users:passwordRequired')}
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required={!editingUser}
                    minLength={6}
                  />
                </div>
                <div className="form-group">
                  <label>{t('common:phone')}</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>{t('common:company')}</label>
                  <select
                    name="companyId"
                    value={formData.companyId}
                    onChange={handleChange}
                  >
                    <option value="">{t('users:selectCompany')}</option>
                    {companies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>{t('common:locations')}</label>
                  <div className="users-location-picker">
                    {selectedLocations.length > 0 && (
                      <div className="users-location-chips">
                        {selectedLocations.map((location) => (
                          <span key={location.id} className="users-location-chip">
                            {location.name}
                            <button
                              type="button"
                              className="users-location-chip-remove"
                              onClick={() => handleRemoveLocation(location.id)}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}

                    <input
                      ref={locationInputRef}
                      type="text"
                      className="users-location-search"
                      value={locationSearch}
                      onChange={handleLocationSearchChange}
                      onFocus={() => setShowLocationDropdown(true)}
                      placeholder={t('users:locationSearchPlaceholder')}
                    />

                    {showLocationDropdown && (
                      <div ref={locationDropdownRef} className="users-location-dropdown">
                        {hasMatches ? (
                          filteredLocations
                            .filter(loc => !selectedLocations.some(sel => sel.id === loc.id))
                            .map((location) => (
                              <div
                                key={location.id}
                                className="users-location-option"
                                onClick={() => handleLocationSelect(location)}
                              >
                                {location.name}
                                {location.address && (
                                  <div className="users-location-option-meta">
                                    {location.address}
                                  </div>
                                )}
                              </div>
                            ))
                        ) : null}
                        
                        {locationSearch.trim() && !exactMatch && (
                          <div
                            className="users-location-create-option"
                            onClick={() => {
                              setNewLocationData({ name: locationSearch.trim(), address: '' });
                              setShowCreateLocationModal(true);
                              setShowLocationDropdown(false);
                            }}
                          >
                            {t('users:createLocationOption', { name: locationSearch.trim() })}
                          </div>
                        )}

                        {!hasMatches && !locationSearch.trim() && (
                          <div className="users-location-empty-hint">
                            {t('users:locationSearchHint')}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <small className="users-field-hint">
                    {t('users:locationFieldHint')}
                  </small>
                </div>
                
                <CreateLocationModal
                  isOpen={showCreateLocationModal}
                  initialName={newLocationData.name}
                  allowBanner={false}
                  companyId={formData.companyId || undefined}
                  onClose={() => {
                    setShowCreateLocationModal(false);
                    setNewLocationData({ name: '', address: '' });
                  }}
                  onSuccess={async (newLocation) => {
                    await fetchLocations();
                    handleLocationSelect(newLocation);
                    setShowCreateLocationModal(false);
                    setNewLocationData({ name: '', address: '' });
                  }}
                />
                <div className="form-group">
                  <label>{t('common:role')}</label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                  >
                    <option value="employee">{t('common:roleEmployee')}</option>
                    <option value="admin">{t('common:roleAdmin')}</option>
                    <option value="reader">{t('common:roleReader')}</option>
                  </select>
                </div>
                {editingUser && (
                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      />
                      {' '}{t('common:active')}
                    </label>
                  </div>
                )}
                <div className="modal-actions">
                  <button type="submit" className="btn btn-primary">
                    {editingUser ? t('common:update') : t('common:create')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCloseModal}
                  >
                    {t('common:cancel')}
                  </button>
                </div>
              </form>
        </Modal>
      </div>
    </div>
  );
};

export default Users;
